import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { requestsAPI, credentialRequestsAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  FileText,
  Package,
  Clock,
  CheckCircle2,
  AlertCircle,
  Plus,
  TrendingUp,
  Users,
  CreditCard,
  BarChart3,
  PieChart,
  Timer,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  DRAFT: 'bg-slate-100 text-slate-700 border-slate-200',
  SUBMITTED: 'bg-blue-100 text-blue-700 border-blue-200',
  REVIEW: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  COUNTERPROPOSAL_SENT: 'bg-purple-100 text-purple-700 border-purple-200',
  COUNTERPROPOSAL_ADJUSTMENT: 'bg-purple-100 text-purple-700 border-purple-200',
  APPROVED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  PENDING_ARTWORKS: 'bg-orange-100 text-orange-700 border-orange-200',
  ARTWORKS_APPROVED: 'bg-teal-100 text-teal-700 border-teal-200',
  CONFIRMED: 'bg-green-100 text-green-700 border-green-200',
};

const statusLabels = {
  DRAFT: 'Borrador',
  SUBMITTED: 'Enviada',
  REVIEW: 'En Revisión',
  COUNTERPROPOSAL_SENT: 'Contrapropuesta Enviada',
  COUNTERPROPOSAL_ADJUSTMENT: 'Ajustando Contrapropuesta',
  APPROVED: 'Aprobada',
  PENDING_ARTWORKS: 'Pendiente Artes',
  ARTWORKS_APPROVED: 'Artes Aprobadas',
  CONFIRMED: 'Confirmada',
};

export default function DashboardPage() {
  const { user, isProvider } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [credentialRequests, setCredentialRequests] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    draft: 0,
    submitted: 0,
    approved: 0,
    avgProcessingDays: 0,
  });
  const [credentialStats, setCredentialStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    totalCredentials: 0,
  });
  const [requestsByProvider, setRequestsByProvider] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [requestsRes, credentialsRes] = await Promise.all([
        requestsAPI.getAll(),
        credentialRequestsAPI.getAll()
      ]);
      
      const requestsData = requestsRes.data;
      const credentialsData = credentialsRes.data;
      
      setRequests(requestsData.slice(0, 5));
      setCredentialRequests(credentialsData.slice(0, 3));

      // Calculate stats
      const approvedRequests = requestsData.filter(r => ['APPROVED', 'CONFIRMED'].includes(r.status));
      let totalDays = 0;
      let countWithDates = 0;
      
      approvedRequests.forEach(req => {
        if (req.created_at && req.approved_at) {
          const created = new Date(req.created_at);
          const approved = new Date(req.approved_at);
          const days = Math.ceil((approved - created) / (1000 * 60 * 60 * 24));
          totalDays += days;
          countWithDates++;
        }
      });

      setStats({
        total: requestsData.length,
        draft: requestsData.filter(r => r.status === 'DRAFT').length,
        submitted: requestsData.filter(r => ['SUBMITTED', 'REVIEW'].includes(r.status)).length,
        approved: approvedRequests.length,
        avgProcessingDays: countWithDates > 0 ? Math.round(totalDays / countWithDates) : 3,
      });

      // Credential stats
      setCredentialStats({
        total: credentialsData.length,
        pending: credentialsData.filter(c => ['PENDING_PAYMENT', 'PENDING_APPROVAL'].includes(c.status)).length,
        approved: credentialsData.filter(c => c.status === 'APPROVED').length,
        totalCredentials: credentialsData.reduce((sum, c) => sum + (c.num_credentials || 0), 0),
      });

      // Group by provider (for admin view)
      if (user?.role === 'ADMIN') {
        const byProvider = {};
        requestsData.forEach(req => {
          const providerName = req.provider_company_name || 'Sin Proveedor';
          if (!byProvider[providerName]) {
            byProvider[providerName] = { name: providerName, count: 0, approved: 0 };
          }
          byProvider[providerName].count++;
          if (['APPROVED', 'CONFIRMED'].includes(req.status)) {
            byProvider[providerName].approved++;
          }
        });
        setRequestsByProvider(Object.values(byProvider).sort((a, b) => b.count - a.count).slice(0, 5));
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isAdmin = user?.role === 'ADMIN';

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900 mb-2">
          ¡Bienvenido, {user?.name}!
        </h1>
        <p className="text-lg text-slate-600">
          {isProvider
            ? 'Gestiona tus solicitudes de espacios publicitarios'
            : 'Panel de control de solicitudes'}
        </p>
      </div>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="border-slate-200 hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Solicitudes</CardTitle>
            <FileText className="h-5 w-5 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{stats.total}</div>
            <p className="text-xs text-slate-500 mt-1">Extravisibilidad</p>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">En Proceso</CardTitle>
            <Clock className="h-5 w-5 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-yellow-600">{stats.submitted}</div>
            <p className="text-xs text-slate-500 mt-1">Pendientes de revisión</p>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Aprobadas</CardTitle>
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-600">{stats.approved}</div>
            <p className="text-xs text-slate-500 mt-1">Confirmadas</p>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:shadow-md transition-shadow bg-gradient-to-br from-primary/5 to-transparent">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Tiempo Promedio</CardTitle>
            <Timer className="h-5 w-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{stats.avgProcessingDays}</div>
            <p className="text-xs text-slate-500 mt-1">Días para aprobar</p>
          </CardContent>
        </Card>
      </div>

      {/* Admin Analytics Section */}
      {isAdmin && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Status Distribution */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <PieChart className="w-5 h-5 text-primary" />
                Distribución por Estado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-slate-400"></div>
                    <span className="text-sm">Borradores</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{stats.draft}</span>
                    <div className="w-24 bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-slate-400 h-2 rounded-full" 
                        style={{ width: `${stats.total ? (stats.draft / stats.total) * 100 : 0}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <span className="text-sm">En Proceso</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{stats.submitted}</span>
                    <div className="w-24 bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-yellow-500 h-2 rounded-full" 
                        style={{ width: `${stats.total ? (stats.submitted / stats.total) * 100 : 0}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                    <span className="text-sm">Aprobadas</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{stats.approved}</span>
                    <div className="w-24 bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-emerald-500 h-2 rounded-full" 
                        style={{ width: `${stats.total ? (stats.approved / stats.total) * 100 : 0}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Providers */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users className="w-5 h-5 text-primary" />
                Top Proveedores
              </CardTitle>
            </CardHeader>
            <CardContent>
              {requestsByProvider.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Sin datos de proveedores</p>
              ) : (
                <div className="space-y-3">
                  {requestsByProvider.map((provider, index) => (
                    <div key={provider.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">
                          {index + 1}
                        </span>
                        <span className="text-sm truncate max-w-[120px]">{provider.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {provider.count} sol.
                        </Badge>
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                          {provider.approved} aprob.
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Credentials Stats */}
          <Card className="border-slate-200 bg-gradient-to-br from-blue-50 to-transparent">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CreditCard className="w-5 h-5 text-blue-600" />
                Credenciales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-white rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{credentialStats.total}</p>
                    <p className="text-xs text-slate-500">Solicitudes</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg">
                    <p className="text-2xl font-bold text-emerald-600">{credentialStats.totalCredentials}</p>
                    <p className="text-xs text-slate-500">Credenciales</p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4 text-yellow-500" />
                    Pendientes: {credentialStats.pending}
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Aprobadas: {credentialStats.approved}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Requests */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200" data-testid="recent-requests-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Solicitudes de Extravisibilidad</CardTitle>
              <CardDescription>Últimas solicitudes</CardDescription>
            </div>
            {isProvider && (
              <Button
                onClick={() => navigate('/requests/create')}
                data-testid="create-request-button"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Nueva
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {requests.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-3">No hay solicitudes</p>
                {isProvider && (
                  <Button onClick={() => navigate('/requests/create')} variant="outline" size="sm">
                    Crear Primera
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {requests.map((request) => (
                  <div
                    key={request.id}
                    className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:border-primary hover:shadow-sm transition-all cursor-pointer"
                    onClick={() => navigate(`/requests/${request.id}`)}
                    data-testid={`request-item-${request.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">
                          {request.requested_month}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(request.created_at).toLocaleDateString('es-EC')}
                        </p>
                      </div>
                    </div>
                    <Badge className={`${statusColors[request.status]} text-xs`}>
                      {statusLabels[request.status]}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Credential Requests */}
        <Card className="border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Solicitudes de Credenciales</CardTitle>
              <CardDescription>Últimas solicitudes</CardDescription>
            </div>
            {isProvider && (
              <Button
                onClick={() => navigate('/credentials/create')}
                size="sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Nueva
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {credentialRequests.length === 0 ? (
              <div className="text-center py-8">
                <CreditCard className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-3">No hay solicitudes</p>
                {isProvider && (
                  <Button onClick={() => navigate('/credentials/create')} variant="outline" size="sm">
                    Crear Primera
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {credentialRequests.map((request) => (
                  <div
                    key={request.id}
                    className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:border-primary hover:shadow-sm transition-all cursor-pointer"
                    onClick={() => navigate(`/credentials/${request.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm truncate max-w-[150px]">
                          {request.company_name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {request.num_credentials} credenciales
                        </p>
                      </div>
                    </div>
                    <Badge className={`text-xs ${
                      request.status === 'APPROVED' ? 'bg-emerald-100 text-emerald-700' :
                      request.status === 'REJECTED' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {request.status === 'APPROVED' ? 'Aprobada' :
                       request.status === 'REJECTED' ? 'Rechazada' :
                       request.status === 'PENDING_APPROVAL' ? 'Por Aprobar' : 'Pendiente'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
